import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';

import { filter, map, take } from 'rxjs/operators';

import { APP_PATHS_SLASH } from '@app/core/enums/paths/app.paths';
import type { UserRole } from '@interfaces/settings.interface';
import { AuthStore } from '@store/auth.store';

/**
 * Enforces a route's `data.roles` against the current user's role, waiting for the host resource
 * to settle first so a cold deep-link decides on the resolved role rather than a transient null.
 * Fails open when the route declares no roles or the role is absent (the hostConfig wiring is
 * prepared but may be unset), so the backend stays the real authorization boundary.
 */
export const roleGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  const allowedRoles = route.data['roles'] as readonly UserRole[] | undefined;
  if (!allowedRoles?.length) {
    return true;
  }

  const decide = (): boolean => {
    const role = authStore.currentUserRole();
    if (!role || allowedRoles.includes(role)) {
      return true;
    }
    void router.navigate([APP_PATHS_SLASH.HOME]);
    return false;
  };

  if (!authStore.hostIsLoading()) {
    return decide();
  }

  return toObservable(authStore.hostIsLoading).pipe(
    filter(loading => !loading),
    take(1),
    map(decide)
  );
};
